import React, { useState, useEffect, useReducer, useCallback } from 'react';
import { systemBus, EVENTS } from '../services/systemBus';
import MobileModal from './MobileModal';

const VaultEditDialog = ({
  editingVault,
  setEditingVault,
  vaults,
  setVaults,
  vaultGridSize
}) => {
  // [UI-MOBILEMODAL-ADOPTION]: MobileModal registers useModalEscape internally
  // (avoid double-registration); handleClose is passed through as its onClose.
  const handleClose = useCallback(() => setEditingVault(null), [setEditingVault]);

  const vault = editingVault ? vaults.find(v => v.id === editingVault) : null;

  const [vaultName, setVaultName] = useState(vault?.name || '');
  const [vaultLocation, setVaultLocation] = useState(vault?.location || '');
  const [vaultCost, setVaultCost] = useState(vault?.cost?.toString() || '0');
  const [vaultGridWidth, setVaultGridWidth] = useState((vault?.gridSize?.[0] || vaultGridSize[0]).toString());
  const [vaultGridHeight, setVaultGridHeight] = useState((vault?.gridSize?.[1] || vaultGridSize[1]).toString());

  // [PHASE 5 SESSION 4]: Force re-render mechanism for reactive vault editing
  // [PURPOSE]: Enables dialog to react to vault edits and removals from other sources
  // [WHY]: When vaults are edited/removed elsewhere, this dialog needs to update or close
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // [PHASE 5 SESSION 4]: Event subscriptions for reactive vault editing dialog
  // [PURPOSE]: Subscribe to vault modification events for real-time sync
  // [WHY]: This dialog needs to show real-time updates when:
  //   - Vault being edited is modified from another source (party sync)
  //   - Vault is removed while being edited (should close dialog)
  //   - Vault properties change from external updates
  // [INTEGRATION]: Ensures vault editor reflects current vault state
  useEffect(() => {
    const handleVaultEvent = (eventName, data) => {

      // If the vault being edited was removed, close the dialog
      if (eventName === 'VAULT_REMOVED' && data.vaultId === editingVault) {
        setEditingVault(null);
        return;
      }

      // Force re-render to show updated vault properties
      forceUpdate();
    };

    // [VAULT_EDITED]: Vault properties were edited (from any source)
    const unsubscribeEdited = systemBus.on(EVENTS.VAULT_EDITED, (data) =>
      handleVaultEvent('VAULT_EDITED', data));

    // [VAULT_REMOVED]: Vault was deleted (close dialog if it's the one being edited)
    const unsubscribeRemoved = systemBus.on(EVENTS.VAULT_REMOVED, (data) =>
      handleVaultEvent('VAULT_REMOVED', data));

    return () => {
      unsubscribeEdited();
      unsubscribeRemoved();
    };
  }, [editingVault, setEditingVault]);

  if (!editingVault || !vault) return null;

  const handleSave = () => {
    // [PHASE 6A]: Track old properties for event emission
    const oldVault = vaults.find(v => v.id === editingVault);
    const oldProperties = oldVault ? {
      name: oldVault.name,
      location: oldVault.location,
      cost: oldVault.cost,
      gridSize: oldVault.gridSize
    } : null;

    const newProperties = {
      name: vaultName,
      location: vaultLocation,
      cost: parseFloat(vaultCost) || 0,
      gridSize: [parseInt(vaultGridWidth) || 8, parseInt(vaultGridHeight) || 8]
    };

    // Update the vault
    setVaults(prev => prev.map(v =>
      v.id === editingVault ? {
        ...v,
        ...newProperties
      } : v
    ));

    // [PHASE 6A]: Emit VAULT_EDITED event for reactive updates
    // PURPOSE: Notify all subscribed components that vault properties have changed
    // WHY: Enables real-time sync across multiple interfaces and party members
    systemBus.emit(EVENTS.VAULT_EDITED, {
      vault: { ...oldVault, ...newProperties },
      vaultId: editingVault,
      oldProperties,
      newProperties,
      changes: {
        name: oldProperties?.name !== newProperties.name,
        location: oldProperties?.location !== newProperties.location,
        cost: oldProperties?.cost !== newProperties.cost,
        gridSize: JSON.stringify(oldProperties?.gridSize) !== JSON.stringify(newProperties.gridSize)
      }
    });

    setEditingVault(null);
  };

  return (
    <MobileModal
      isOpen={true}
      onClose={handleClose}
      title="Edit Vault"
      onSave={handleSave}
      overlayClassName="z-[90]"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-gray-300 mb-1">Vault Name</label>
          <input
            type="text"
            value={vaultName}
            onChange={(e) => setVaultName(e.target.value)}
            className="w-full p-2 bg-gray-800 text-white rounded"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-300 mb-1">Location</label>
          <input
            type="text"
            value={vaultLocation}
            onChange={(e) => setVaultLocation(e.target.value)}
            className="w-full p-2 bg-gray-800 text-white rounded"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-300 mb-1">Access Cost (gp)</label>
          <input
            type="number"
            step="0.01"
            value={vaultCost}
            onChange={(e) => setVaultCost(e.target.value)}
            className="w-full p-2 bg-gray-800 text-white rounded"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-300 mb-1">Grid Size</label>
          <div className="flex gap-2 items-center">
            <input
              type="number"
              min="4"
              max="20"
              value={vaultGridWidth}
              onChange={(e) => setVaultGridWidth(e.target.value)}
              className="w-20 p-2 bg-gray-800 text-white rounded"
              placeholder="Width"
            />
            <span className="text-gray-400">×</span>
            <input
              type="number"
              min="4"
              max="20"
              value={vaultGridHeight}
              onChange={(e) => setVaultGridHeight(e.target.value)}
              className="w-20 p-2 bg-gray-800 text-white rounded"
              placeholder="Height"
            />
            <span className="text-gray-400 text-xs">
              = {(parseInt(vaultGridWidth) || 8) * (parseInt(vaultGridHeight) || 8)} slots
            </span>
          </div>
        </div>
      </div>
    </MobileModal>
  );
};

export default VaultEditDialog;
